"use client";

import type { RichTextComponent } from "../../types";
import { neutralizeDeadAnchors, sanitizeRichTextHtml } from "../../utils/richTextHtml";
import { expandInlineIcons } from "../../utils/inlineIcons";
import { isLivenessCheckableHref, normalizeHrefKey } from "../../utils/links";
import { useRendererRuntime } from "../../runtime/RendererRuntime";
import { useDevPlaceholder } from "../../runtime/DevPlaceholder";

/**
 * RichText block — renders legacy editorial body HTML (institutional/basic
 * pages, governor bios, banknote pages) faithfully: headings, lists, links,
 * images, quotes and tables all survive, unlike `TextBlock` whose paragraph
 * sanitiser strips them.
 *
 * The body HTML is run through `sanitizeRichTextHtml` (the ONLY path it reaches
 * the DOM) and injected via `dangerouslySetInnerHTML`. Inline `<img>` tags are
 * not React components, so their bucket-relative `src` keys are resolved here
 * through the runtime `assetResolver` — the same env-aware resolution
 * `DataRecordPdfPreview` applies to PDFs. This is a **client component** so it
 * can read the runtime context; new-site typography/colours (headings primary
 * blue `#005CA9`, body navy `#0F2849`, links `#005CA9`) are applied via the
 * shared `rich-paragraph-block` styling and a scoped `rich-text-block` class.
 */

/** True when a src is already a browser-loadable absolute URL. */
function isAbsoluteUrl(src: string): boolean {
  return /^(?:https?:)?\/\//i.test(src) || /^(?:data|blob):/i.test(src);
}

/**
 * Rewrites inline `<img src="...">` keys through the asset resolver so legacy
 * storage keys become environment-aware absolute URLs. Already-absolute URLs
 * are still passed to the resolver (it passes them through unchanged).
 */
function resolveInlineImages(
  html: string,
  resolver?: (key: string) => string,
): string {
  if (!resolver) return html;
  return html.replace(
    /(<img\b[^>]*?\bsrc=")([^"]*)(")/gi,
    (_match, pre: string, src: string, post: string) => {
      const trimmed = src.trim();
      if (!trimmed) return `${pre}${src}${post}`;
      const resolved = resolver(trimmed);
      return `${pre}${resolved}${post}`;
    },
  );
}

/** RichText renderer for migrated editorial body HTML. */
export function RichTextRenderer({ component }: { component: RichTextComponent }) {
  const { assetResolver } = useRendererRuntime();
  const { previewMode, liveHrefs, notify } = useDevPlaceholder();
  const raw = component.content?.html ?? "";
  const sanitized = expandInlineIcons(sanitizeRichTextHtml(raw));
  const resolved = resolveInlineImages(sanitized, assetResolver);
  // Same fail-open verdict as useGuardedHref, applied to inline anchors: dead links
  // become plain text in production, dev-modal triggers in preview.
  const html = neutralizeDeadAnchors(resolved, {
    previewMode,
    isLive: (href) =>
      !isLivenessCheckableHref(href) || liveHrefs === null || liveHrefs.has(normalizeHrefKey(href)),
  });

  if (!html) return null;

  const onPlaceholderActivate = (event: React.SyntheticEvent<HTMLElement>) => {
    const target = (event.target as HTMLElement).closest?.("[data-dev-placeholder]");
    if (!target || !event.currentTarget.contains(target)) return;
    event.preventDefault();
    notify(target.textContent?.trim() ?? "");
  };

  return (
    <section
      className="rich-text-block rich-paragraph-block text-base xl:text-lg text-[#0F2849] [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-[#005CA9] [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-[#005CA9] [&_h4]:mt-5 [&_h4]:mb-2 [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-[#005CA9] [&_p]:mb-4 [&_a]:text-[#005CA9] [&_a]:underline [&_a:hover]:text-[#0F2849] [&_[data-dev-placeholder]]:text-[#005CA9] [&_[data-dev-placeholder]]:underline [&_[data-dev-placeholder]]:cursor-pointer [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1 [&_blockquote]:my-5 [&_blockquote]:border-l-4 [&_blockquote]:border-[#005CA9] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#0F2849] [&_table]:my-5 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-slate-200 [&_th]:bg-[#F4F8FC] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_td]:border [&_td]:border-slate-200 [&_td]:px-3 [&_td]:py-2 [&_img]:my-4 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded [&_hr]:my-6 [&_hr]:border-slate-200"
      onClick={onPlaceholderActivate}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onPlaceholderActivate(event);
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
